import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, signal, WritableSignal } from '@angular/core';
import {
  disabled,
  form,
  FormField,
  maxLength,
  minLength,
  readonly,
  required,
  validate,
} from '@angular/forms/signals';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Genders, UserIdentityDto } from '@workspace/data-types';
import { GenderDisplayPipe } from '@workspace/ng-data-access';
import { DateTime } from 'luxon';
import { AbstractSignalFormComponent } from '../../../abstract';

export interface PersonIdentityFormModel {
  firstName: string;
  lastName: string;
  gender: Genders | null;
  birthDate: string | null;
}

@Component({
  selector: 'aura-person-identity-form',
  imports: [
    FormField,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    GenderDisplayPipe,
    TitleCasePipe,
    MatDatepickerModule,
  ],
  templateUrl: './person-identity-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraPersonIdentityFormComponent extends AbstractSignalFormComponent<
  Partial<UserIdentityDto>,
  PersonIdentityFormModel
> {
  allGenders = Object.values(Genders);
  columns = signal<1 | 2 | 3>(2);
  showMode = input<'all' | 'names' | 'demographics'>('all');

  override model: WritableSignal<PersonIdentityFormModel> = signal<PersonIdentityFormModel>({
    firstName: '',
    lastName: '',
    gender: null,
    birthDate: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.firstName, {
      when: () => (this.showMode() === 'all' || this.showMode() === 'names') && this.isRequired('firstName'),
    });
    minLength(b.firstName, 2);
    maxLength(b.firstName, 30);
    validate(b.firstName, (ctx) => {
      const val = ctx.value();
      if (!val) return null;
      if (/\s/.test(val)) {
        return {
          kind: 'noWhitespace',
          message: 'Les espaces ne sont pas autorisés. Utilisez un tiret pour les prénoms composés.',
        };
      }
      if (/[^\p{L}'-]/u.test(val)) {
        return {
          kind: 'invalidCharacters',
          message: 'Seules les lettres, les accents et les tirets sont autorisés.',
        };
      }
      if (/^[-']|[-']$/.test(val)) {
        return {
          kind: 'trailingPunctuation',
          message: 'Le prénom ne peut pas commencer ou se terminer par un tiret ou une apostrophe.',
        };
      }
      if (/[-']{2,}/.test(val)) {
        return {
          kind: 'consecutivePunctuation',
          message: 'Les tirets consécutifs ne sont pas autorisés.',
        };
      }
      return null;
    });
    readonly(b.firstName, { when: () => this.allReadonly() });

    required(b.lastName, {
      when: () => (this.showMode() === 'all' || this.showMode() === 'names') && this.isRequired('lastName'),
    });
    minLength(b.lastName, 2);
    maxLength(b.lastName, 30);
    validate(b.lastName, (ctx) => {
      const val = ctx.value();
      if (!val) return null;
      if (/\s/.test(val)) {
        return {
          kind: 'noWhitespace',
          message: 'Les espaces ne sont pas autorisés. Utilisez un tiret pour les noms composés.',
        };
      }
      if (/[^\p{L}'-]/u.test(val)) {
        return {
          kind: 'invalidCharacters',
          message: 'Seules les lettres, les accents et les tirets sont autorisés.',
        };
      }
      if (/^[-']|[-']$/.test(val)) {
        return {
          kind: 'trailingPunctuation',
          message: 'Le nom ne peut pas commencer ou se terminer par un tiret ou une apostrophe.',
        };
      }
      if (/[-']{2,}/.test(val)) {
        return {
          kind: 'consecutivePunctuation',
          message: 'Les tirets consécutifs ne sont pas autorisés.',
        };
      }
      return null;
    });
    readonly(b.lastName, { when: () => this.allReadonly() });

    required(b.gender, {
      when: () =>
        (this.showMode() === 'all' || this.showMode() === 'demographics') && this.isRequired('gender'),
    });
    disabled(b.gender, { when: () => this.allReadonly() || this.allDisable() });

    required(b.birthDate, {
      when: () =>
        (this.showMode() === 'all' || this.showMode() === 'demographics') && this.isRequired('birthDate'),
    });
    readonly(b.birthDate, { when: () => this.allReadonly() });
  });

  onNameKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) {
      return;
    }
    if (!/^[\p{L}'-]$/u.test(event.key)) {
      event.preventDefault();
    }
  }

  preventSpace(event: KeyboardEvent): void {
    this.onNameKeyDown(event);
  }

  onNamePaste(event: ClipboardEvent, field: 'firstName' | 'lastName'): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    let cleanedText = pastedText
      .normalize('NFC')
      .replace(/[’‘`]/g, "'")
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\p{L}'-]/gu, '')
      .replace(/-{2,}/g, '-');

    while (cleanedText.startsWith('-') || cleanedText.startsWith("'")) {
      cleanedText = cleanedText.slice(1);
    }
    while (cleanedText.endsWith('-') || cleanedText.endsWith("'")) {
      cleanedText = cleanedText.slice(0, -1);
    }

    this.model.update((m) => ({ ...m, [field]: cleanedText }));
  }

  onPasteNoSpace(event: ClipboardEvent, field: 'firstName' | 'lastName'): void {
    this.onNamePaste(event, field);
  }

  protected override buildModelFromDto(dto: Partial<UserIdentityDto>): PersonIdentityFormModel {
    return {
      firstName: dto.firstName ?? '',
      lastName: dto.lastName ?? '',
      gender: dto.gender ?? null,
      birthDate: dto.birthDate ?? null,
    };
  }

  protected override buildDtoFromModel(model: PersonIdentityFormModel): Partial<UserIdentityDto> {
    const result: Partial<UserIdentityDto> = {};
    if (this.showMode() === 'all' || this.showMode() === 'names') {
      if (model.lastName) result.lastName = model.lastName.normalize('NFC').trim();
      if (model.firstName) result.firstName = model.firstName.normalize('NFC').trim();
    }
    if (this.showMode() === 'all' || this.showMode() === 'demographics') {
      if (model.gender) result.gender = model.gender;
      const formattedDate = this.formatLocalDate(model.birthDate);
      if (formattedDate) result.birthDate = formattedDate;
    }
    return result;
  }

  private formatLocalDate(date: Date | DateTime | string | null): string | null {
    if (!date) return null;

    if (DateTime.isDateTime(date)) {
      return date.toFormat('yyyy-MM-dd');
    }

    if (date instanceof Date) {
      return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
    }

    if (typeof date === 'string') {
      return date;
    }

    return null;
  }
}
